import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCheckSession } from "src/authentication/data-access/useLoginQuery";

const AuthContext = createContext<IUseAuth | null>(null);
type TUserSession = {
  activateDeviceForNotifications: boolean | null;
  activeOwnRegionApplicationsCount: number | null;
  activeRegionApplicationsCount: number | null;
  chamberDescr: string | null;
  chamberId: number | null;
  coAfm: null;
  coChamberDescr: null;
  coChamberDescrEn: null;
  coChamberEmail: null;
  coChamberId: null;
  coChamberNumber: null;
  coChamberPhones: null;
  coChamberRec: null;
  coChamberStreet: null;
  coId: null;
  coLegalTypeDescr: null;
  coLegalTypeId: null;
  coName: null;
  coTitle: null;
  email: string | null;
  firstName: string | null;
  hasCompanyContact: null;
  hasContactData: boolean | null;
  id: number;
  isBusiness: boolean;
  isCashier: boolean;
  isChamber: boolean;
  isET: boolean;
  isGGEAdmin: boolean;
  isFromEIDAS: boolean;
  isImpersonation: boolean;
  isNotary: null;
  isOrgAdmin: boolean;
  isPublicComp: null;
  isRegion: boolean;
  isRemoved: null;
  isSystemAdmin: boolean;
  lastName: string;
  oldSession: null;
  pastOwnRegionApplicationsCount: number | null;
  regionId: null;
  regionName: null;
  username: string;
  isNonGemiUser: boolean;
  afm: string;
  isCompanyPerson: boolean;
  companyStatusId: number | null;
  companyStatusDescription: string | null;
  companyStatusDescriptionEn: string | null;
  expiryDate: string | null;
  isAoristi: boolean | null;
  isSuspended: boolean | null;
  suspensionReason: string[] | null;
  suspensionReasonEn: string[] | null;
  chamberHasOneAdmin: boolean;
  isSupervisorAssigner: boolean;
  isSupervisorAssignee: boolean;
};
interface IUserInfo {
  m01_id: number;
  m01_e03_id: number;
  m01_username: string;
  m01_email: string;
  m01_passwordhashed: string;
  m01_salt: string;
  m01_afm: string;
  m01_lastname: string;
  m01_firstname: string;
  m01_phone: string;
  m01_isenabled: boolean;
  m01_registrationdate: string;
  m01_lastlogindate: string;
  m01_isfromchamber: boolean;
  m01_ispubliccomp: boolean;
  M01_DATE_CREATED: string;
  M01_LAST_UPDATED: string;
  m01_iban: string;
  m01_mobilephone: string;
}
interface IUseAuth {
  authenticated: boolean;
  isChamber: boolean;
  isOrgAdmin: boolean;
  isBusiness: boolean;
  isCashier: boolean;
  isRegion: boolean;
  userInfo: TUserSession;
  isLoading: boolean;
  isNonGemiUser: boolean;
  isSystemAdmin: boolean;
  userHomePath: string;
  activateDeviceForNotifications: boolean;
  isDy: boolean;
  isRemoved: boolean;
  isGGE: boolean;
  isGGEAdmin: boolean;
  isKEE: boolean;
  isSampleInitiator: boolean;
  isChamberOnly: boolean;
  chamberHasOneAdmin: boolean;
  isSupervisorAssigner: boolean;
  isSupervisorAssignee: boolean;
  isCompanyPerson: boolean;
  sessionTimedOut: boolean;
  setSessionTimedOut: React.Dispatch<React.SetStateAction<boolean>>;
  userCantAccess: boolean;
  setUserCantAccess: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useAuth(): IUseAuth {
  return useContext(AuthContext as any);
}

export function AuthProvider({ children }: any) {
  const { data, isLoading } = useCheckSession();
  const [sessionTimedOut, setSessionTimedOut] = useState(data?.status == 401);
  const [userCantAccess, setUserCantAccess] = useState<boolean>(false);

  useEffect(() => {
    if (data?.data) {
      localStorage.setItem("userInfo", JSON.stringify(data?.data));
    }
  }, [data]);

  const contextValue = {
    authenticated:
      data?.data?.session?.isChamber ||
      data?.data?.session?.isBusiness ||
      data?.data?.session?.isNonGemiUser ||
      data?.data?.session?.isRegion ||
      data?.data?.session?.isSystemAdmin ||
      data?.data?.session?.isOrgAdmin ||
      data?.data?.session?.isCashier ||
      data?.data?.session?.isGGEAdmin ||
      data?.data?.session?.isGGE ||
      data?.data.session?.isCompanyPerson ||
      data?.data?.session?.isSupervisorAssignee ||
      data?.data?.session?.isSupervisorAssigner,
    isChamber: data?.data?.session?.isChamber,
    isOrgAdmin: data?.data?.session?.isOrgAdmin,
    isRegion: data?.data?.session?.isRegion,
    isBusiness: data?.data?.session?.isBusiness,
    isNonGemiUser: data?.data?.session?.isNonGemiUser,
    isSystemAdmin: data?.data?.session?.isSystemAdmin,
    isCashier: data?.data?.session?.isCashier,
    userInfo: data?.data?.session,
    isGGE:
      data?.data?.session?.coChamberId == 141 ||
      data?.data?.session?.chamberId == 141,
    isKEE:
      data?.data?.session?.coChamberId == 999 ||
      data?.data?.session?.chamberId == 999,
    isSampleInitiator: data?.data?.session?.isSampleInitiator,
    isChamberOnly: data?.data?.session?.isChamberOnly,
    isSupervisorAssigner: data?.data?.session?.isSupervisorAssigner,
    isSupervisorAssignee: data?.data?.session?.isSupervisorAssignee,
    isGGEAdmin: data?.data?.session?.isGGEAdmin,
    isCompanyPerson: data?.data?.session?.isCompanyPerson,
    userHomePath: data?.data?.session?.isBusiness
      ? "/business/dashboard"
      : data?.data?.session?.isChamber
        ? "/chamber/dashboard"
        : data?.data?.session?.isNonGemiUser
          ? "/nonGemi/dashboard"
          : data?.data?.session?.isRegion
            ? "/region/dashboard"
            : data?.data?.session?.isSystemAdmin ||
                data?.data?.session?.isGGEAdmin
              ? "/admin/applications"
              : data?.data?.session?.isCashier
                ? "/cashier/dashboard"
                : data?.data?.session?.isCompanyPerson
                  ? "/namePurchase/approverRequestView"
                  : data?.data?.session?.isSupervisorAssignee &&
                      !data?.data?.session?.isChamber
                    ? "/chamber/dashboard"
                    : data?.data?.session?.isSupervisorAssigner &&
                        !data?.data?.session?.isChamber
                      ? "/chamber/dashboard"
                      : "/",
    isLoading,
    activateDeviceForNotifications:
      data?.data?.session?.activateDeviceForNotifications,
    isDy: data?.data?.session?.isDy,
    isRemoved: data?.data?.session?.isRemoved,
    sessionTimedOut: sessionTimedOut,
    chamberHasOneAdmin: data?.data?.session?.chamberHasOneAdmin,
    setSessionTimedOut: setSessionTimedOut,
    userCantAccess,
    setUserCantAccess,
    companyStatusId: data?.data?.session?.companyStatusId,
    companyStatusDescription: data?.data?.session?.companyStatusDescription,
    companyStatusDescriptionEn: data?.data?.session?.companyStatusDescriptionEn,
    expiryDate: data?.data?.session?.expiryDate,
    isAoristi: data?.data?.session?.isAoristi,
    isSuspended: data?.data?.session?.isSuspended,
    suspensionReason: data?.data?.session?.suspensionReason,
    suspensionReasonEn: data?.data?.session?.suspensionReasonEn,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}
